<script setup lang="ts">
/**
 * One tile of a ribbon's style gallery (Write's „Formatvorlagen“): a sample of
 * the style on top, its name below, pressed while the caret's paragraph has
 * that style. `level` gives the sample the style's own look — body text, a
 * heading 1–3 or a document-defined (`custom`) style.
 *
 * The gallery around the tiles — rows, paging — is the host's layout; a tile
 * only sizes itself. In the shared document workspace (`data-office-view` on
 * an ancestor) it takes the workspace look and lets the gallery's grid column
 * set its width.
 */
export type NxRibbonStyleTileLevel = 0 | 1 | 2 | 3 | 'custom';

const props = withDefaults(
  defineProps<{
    /** The style's name, under the sample. */
    label: string;
    /** The sample text; defaults to the label. */
    sample?: string;
    level?: NxRibbonStyleTileLevel;
    /** The caret's paragraph has this style. */
    pressed?: boolean;
    disabled?: boolean;
  }>(),
  { sample: undefined, level: 0, pressed: false, disabled: false }
);
</script>

<template>
  <button
    type="button"
    class="nx-ribbon-style-tile"
    :class="[`level-${props.level}`, { 'is-on': props.pressed }]"
    :aria-pressed="props.pressed"
    :disabled="props.disabled"
  >
    <span class="nx-ribbon-style-tile__sample">{{ props.sample ?? props.label }}</span>
    <small class="nx-ribbon-style-tile__label">{{ props.label }}</small>
  </button>
</template>

<style scoped>
.nx-ribbon-style-tile {
  box-sizing: border-box;
  width: calc(var(--nxt-size-unit) * 4.0625);
  height: calc(var(--nxt-size-unit) * 3.125);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: calc(var(--nxt-size-unit) * 0.1875);
  padding: calc(var(--nxt-size-unit) * 0.25) calc(var(--nxt-size-unit) * 0.375);
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  color: var(--office-ribbon-fg, var(--nxt-fg));
  font: inherit;
  text-align: start;
  cursor: pointer;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out);
}

.nx-ribbon-style-tile:hover:not(:disabled) {
  border-color: var(--office-ribbon-active-border, var(--nxt-brand-border));
  background: var(--office-ribbon-active-bg, var(--nxt-brand-subtle-bg));
}

.nx-ribbon-style-tile.is-on {
  border-color: var(--office-ribbon-active-border, var(--nxt-brand-border));
  background: var(--office-ribbon-active-bg, var(--nxt-brand-subtle-bg));
  color: var(--office-ribbon-hover-fg, var(--nxt-brand-subtle-fg));
}

.nx-ribbon-style-tile:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-ribbon-style-tile:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.nx-ribbon-style-tile__sample {
  max-width: 100%;
  overflow: hidden;
  color: var(--office-ribbon-fg, var(--nxt-fg));
  font-family: Georgia, 'Times New Roman', serif;
  font-size: calc(var(--nxt-size-unit) * 0.75);
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.level-1 .nx-ribbon-style-tile__sample {
  font-size: calc(var(--nxt-size-unit) * 0.9375);
  font-weight: var(--nxt-font-weight-bold);
}

.level-2 .nx-ribbon-style-tile__sample {
  font-size: calc(var(--nxt-size-unit) * 0.8125);
  font-weight: var(--nxt-font-weight-bold);
}

.level-3 .nx-ribbon-style-tile__sample {
  font-weight: var(--nxt-font-weight-semibold);
}

.level-custom .nx-ribbon-style-tile__sample {
  font-family: inherit;
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-ribbon-style-tile__label {
  max-width: 100%;
  overflow: hidden;
  color: var(--office-ribbon-muted-fg, var(--nxt-fg-subtle));
  font-size: calc(var(--nxt-size-unit) * 0.5);
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The shared document workspace: one tile height with the ribbon's other
   large commands, the gallery's grid column as the width, the samples in the
   interface face except Heading 1. */
[data-office-view] .nx-ribbon-style-tile {
  width: auto;
  min-width: calc(var(--nxt-size-unit) * 5.4375);
  height: calc(var(--nxt-size-unit) * 4);
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}

[data-office-view] .nx-ribbon-style-tile__sample {
  font-family: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.25;
}

[data-office-view] .level-1 .nx-ribbon-style-tile__sample {
  font-family: Georgia, serif;
  font-size: var(--nxt-font-size-xl);
}

[data-office-view] .level-2 .nx-ribbon-style-tile__sample {
  font-size: var(--nxt-font-size-lg);
}

[data-office-view] .nx-ribbon-style-tile__label {
  font-size: calc(var(--nxt-size-unit) * 0.625);
  line-height: 1.2;
}

@media (prefers-reduced-motion: reduce) {
  .nx-ribbon-style-tile {
    transition: none;
  }
}
</style>
